import { Component, computed, inject, linkedSignal, resource, signal, viewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalDialogComponent } from 'src/app/controls/components/advanced/modal-dialog/modal-dialog.component';
import { ButtonComponent } from 'src/app/controls/components/basic/button/button.component';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { SingleselectboxComponent } from 'src/app/controls/components/basic/singleselectbox/singleselectbox.component';
import { ButtonPrimaryDirective } from 'src/app/controls/directives/button-primary.directive';
import { ButtonSecondaryDirective } from 'src/app/controls/directives/button-secondary.directive';
import { PartnerOnboardingService } from 'src/app/partner/services/partner-onboarding.service';
import { OnboardingFile } from 'src/app/partnerportal/components/onboarding-file-selector/models/onboarding-file';
import { As2Certificate, Transport } from 'src/app/partnerportal/models/partner-onboarding';

@Component({
  selector: 'app-as2-transport-editor',
  standalone: true,
  imports: [ModalDialogComponent, FormsModule, SingleselectboxComponent, ButtonComponent, ButtonPrimaryDirective, ButtonSecondaryDirective, ModalDialogComponent, LayoutColumnComponent, LayoutRowComponent],
  templateUrl: './as2-transport-editor.component.html',
  styleUrl: './as2-transport-editor.component.scss'
})
export class As2TransportEditorComponent {
  private dialog = inject(NgbActiveModal);
  private partnerOnboardingService = inject(PartnerOnboardingService);
  form = viewChild<NgForm>('form');

  transportOriginal = signal<Transport>(null);
  transportInternal = signal<Transport>(null);
  public dialogResult = signal<As2TransportEditorDialogResult>(null);

  public set transport(transport: Transport) {
    this.transportOriginal.set(transport);
    this.transportInternal.set(transport);
  }

  // Load AS2 details (AS2 URL, list of AS2 IDs, list of HTTP handlers) from the service. 
  // This is needed to populate the dropdowns and display the connection summary. 
  // The details are loaded once when the editor is opened, and then used for any changes the user makes in the editor until they accept or cancel.
  as2DetailsResource = resource({
    params: () => ({}),
    loader: () => this.partnerOnboardingService.getAs2Details()
  });
  as2Details = computed(() => this.as2DetailsResource.value());


  // For AS2 ID selection, get the list of AS2 IDs from the AS2 details (each AS2 partner can have multiple AS2 IDs), 
  // and determine the currently selected AS2 ID based on the TransportFields
  as2Ids = computed(() => this.as2Details()?.As2Partners.map(p => p.As2Ids.map(id => ({name: id+' ('+p.Name+')', value: id}) )).flat());

  selectedAs2Id = linkedSignal(() => {
    const id = this.transportInternal()?.TransportFields?.find(f => f.Name === 'As2IdCustomer')?.Value;
    return this.as2Ids()?.find(idObj => idObj.value === id)?.value;
  });
  

  // For AS2 HTTP handler selection, get the list of HTTP handlers from the AS2 details, and determine the currently selected HTTP handler based on the TransportFields
  as2Connections = computed(() => {
    const connections: { type: string, name: string, value: string }[] = [
      { type: 'integrated', name: 'AS2 (default)', value: this.as2Details()?.As2Url ?? '' }
    ];
    const handlers = this.as2Details()?.As2HttpHandlers ?? [];
    if (handlers.length > 0) {
      handlers.forEach(handler => {
        connections.push({ type: 'handler', name: handler.Name, value: handler.As2Url });
      });
    }
    return connections;
  });

  selectedConnection = linkedSignal(() => {
    const hostname = this.transportInternal()?.TransportFields?.find(f => f.Name === 'HostnameCustomer')?.Value;
    return this.as2Connections()?.find(h => h.value === hostname)?.value;
  });

  
  idChanged(newId: string) {
    this.selectedAs2Id.set(newId);
  }

  connectionChanged(newConnection: string) {
    this.selectedConnection.set(newConnection);
  }

  missingValues = computed(() => !this.selectedAs2Id() || !this.as2PublicCert());

  hostname = computed(() => {
    const connection = this.as2Connections()?.find(h => h.value === this.selectedConnection());
    return connection?.value;
  });

  as2Id = computed(() => {
    this.selectedAs2Id();
  });

  as2PublicCert = computed(() => {
    const partner = this.as2Details()?.As2Partners.find(p => p.As2Ids.includes(this.selectedAs2Id()));
    const partnerCert = partner?.PublicCertificate;

    const connection = this.as2Connections()?.find(h => h.value === this.selectedConnection());
    if (connection?.type === 'handler') {
      const httpHandler = this.as2Details()?.As2HttpHandlers.find(h => h.As2Url === connection.value);
      return httpHandler?.PublicCertificate ?? partnerCert;
    }
    else {
      return partnerCert;
    }
  });

  result = computed(() => {
    const result: As2TransportResult = {
      transport: this.transportInternal(),
      dialogResult: this.dialogResult()
    }
    return result;
  });

  parseCertificate(certificateData: As2Certificate): OnboardingFile {
    const file : OnboardingFile = {
      FileName: 'as2Certificate.cer',
      Data: certificateData.Data,
      Mime: 'application/x-x509-ca-cert',
    };
    return file;
  }

  getTransportFromForm(): Transport {
    const updatedTransport = new Transport({
      ...this.transportInternal(),
      TransportFields: this.transportInternal()?.TransportFields?.map(field => {
        let value = field.Value;
        if (field.Name === 'As2IdCustomer') {
          value = this.selectedAs2Id();
        }
        if (field.Name === 'HostnameCustomer') {
          value = this.hostname();
        }
        if (field.Name === 'PublicCertificateCustomer') {
          const file = this.as2PublicCert() ? this.parseCertificate(this.as2PublicCert()) : null;
          value = JSON.stringify(file);
        }

        return ({
          ...field,
          Value: value
        });
      }) ?? []
    });
    return updatedTransport;
  }

  accept() {
    const updatedTransport = this.getTransportFromForm();
    this.transportInternal.set(updatedTransport);
    this.dialogResult.set(As2TransportEditorDialogResult.Accept);
    this.dialog.close(this.result());
  }

  cancel() {
    this.dialogResult.set(As2TransportEditorDialogResult.Cancel);
    this.dialog.close(this.result());
  }
}

export interface As2TransportResult {
  transport: Transport;
  dialogResult: As2TransportEditorDialogResult;
}

export enum As2TransportEditorDialogResult {
  Accept,
  Cancel 
}
